import React, { useRef, useEffect, useMemo } from 'react';
import { IconExclamationTriangle, IconBuilding } from '../../../ui-kit/icons';
import { connect } from 'react-redux';
import { selectCurrentDisplayedIncident } from '../../../../state/modules/incidents/selectors';
import Loader from '../../../general/Loader';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import GoogleDirectionAPIWrapper from '../../../../utils/GoogleDirectionAPIWrapper';
import GoogleMapReact from 'google-map-react';

// converts API coordinates to Google Map format
const convertCoords = coords => (coords && coords.latitude && { lat: coords.latitude, lng: coords.longitude }) || null;

const ResponderLocationMarker = ({ name }) => {
  const re = /\b(?:[a-zA-Z]|-)/g;
  const initials = name.match(re)?.join('') || '';
  return (
    <>
      <div
        className="d-flex align-items-center justify-content-center rounded-circle bg-success fw-bold shadow fs-6"
        style={{
          width: '40px',
          height: '40px',
          position: 'absolute',
          transform: 'translate(-50%, -110%)'
        }}
      >
        {initials}
      </div>
      <div
        className="rounded-circle bg-dark"
        style={{
          width: '5px',
          height: '5px',
          position: 'absolute',
          transform: 'translate(-50%, -50%)'
        }}
      ></div>
    </>
  );
};

const IncidentMarker = () => <IconExclamationTriangle className="translate-middle text-danger fa-4x" />;

const StationMarker = () => <IconBuilding className="translate-middle text-danger fa-4x" />;

const LiveIncidentMap = ({ displayedIncident, groups, groupId }) => {
  const incidentCoords = useMemo(() => convertCoords(displayedIncident?.address), [displayedIncident]);
  const group = groups.byId[groupId];
  const stationCoords = useMemo(() => convertCoords(group?.coordinates), [group]);
  const incidentResponses = useMemo(() => displayedIncident?.incident_responses || [], [displayedIncident]);

  const responders = useMemo(
    () =>
      incidentResponses
        .filter(r => r.status === 'acknowledged')
        .map(responder => ({
          id: responder.id,
          name: responder.user_name,
          arrived_at_station: responder.arrived_at_station,
          coordinates: convertCoords(responder.location)
        }))
        .filter(r => r.coordinates),
    [incidentResponses]
  );

  const mapRef = useRef(null); // reference to the Google map
  const mapsRef = useRef(null); // reference to the Google maps library
  const directionsRendererRef = useRef(null); // reference current directionsRenderer on the map

  const onGoogleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    drawDirections();
    setBounds();
  };

  const setBounds = () => {
    if (!mapRef.current) return;

    const responderCoords = responders.map(r => r.coordinates);
    const coordinates = [incidentCoords, stationCoords, ...responderCoords].filter(Boolean);

    const bounds = new mapsRef.current.LatLngBounds();
    // Add all coordinates to the bounds
    for (const coord of coordinates) bounds.extend(new mapsRef.current.LatLng(coord.lat, coord.lng));
    mapRef.current.fitBounds(bounds);
  };

  useEffect(setBounds, [stationCoords, incidentCoords, responders]);

  const drawDirections = async () => {
    if (!stationCoords || !incidentCoords || !mapRef.current) return;

    try {
      const result = await GoogleDirectionAPIWrapper.getInstance().fetch(stationCoords, incidentCoords);

      // ensure there is only one instance of the directionsRenderer, so when
      // directions are updated, the old ones are removed. This is important when
      // switching between incidents
      directionsRendererRef.current =
        directionsRendererRef.current ||
        new mapsRef.current.DirectionsRenderer({
          map: mapRef.current,
          suppressMarkers: true,
          preserveViewport: true
        });

      directionsRendererRef.current.setDirections(result);
    } catch {
      // do nothing
    }
  };

  useEffect(() => {
    drawDirections();
  }, [stationCoords, incidentCoords]);

  if (!groups.lastFetch) return <Loader />;
  if (!displayedIncident) return <WaitingForIncidentMessage />;

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: globalThis.mapsApiKey }}
        defaultCenter={stationCoords}
        defaultZoom={14}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={onGoogleApiLoaded}
        options={{ disableDefaultUI: true }}
      >
        {incidentCoords && <IncidentMarker {...incidentCoords} />}
        {stationCoords && <StationMarker {...stationCoords} />}
        {responders.map(r => (
          <ResponderLocationMarker key={r.id} {...r.coordinates} name={r.name} />
        ))}
      </GoogleMapReact>
    </div>
  );
};

const mapStateToProps = state => ({
  groups: state.groups,
  displayedIncident: selectCurrentDisplayedIncident(state)
});

export default connect(mapStateToProps)(LiveIncidentMap);
